import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Phone, Check, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import Breadcrumbs, { breadcrumbLd } from "@/components/Breadcrumbs";
import Icon from "@/components/Icon";
import { Btn } from "@/components/Btn";
import { Reveal, Stagger, item } from "@/components/Reveal";
import { getCity, cityPath, CITIES } from "@/lib/cities";
import { CITY_CONTENT, hoodSlug } from "@/content/citiesContent";
import { TOP_SERVICES, servicePath } from "@/lib/services";
import { BRAND, CTA } from "@/lib/brand";
import NotFound from "@/pages/NotFound";

export default function CityPage() {
  const { slug } = useParams();
  const city = getCity(slug);
  const content = CITY_CONTENT[slug];
  if (!city || !content) return <NotFound />;

  const idx = CITIES.findIndex((c) => c.slug === slug);
  const nearby = [CITIES[(idx + 1) % CITIES.length], CITIES[(idx + 2) % CITIES.length], CITIES[(idx + 3) % CITIES.length]];
  const crumbs = [{ label: "Home", to: "/" }, { label: "Service Areas", to: "/service-areas" }, { label: `${city.name}, SC` }];
  // Charleston gets its own title and H1 so it does not compete with the homepage.
  const isCharleston = slug === "charleston-sc";
  const title = isCharleston ? "Junk Hauling & Cleanouts in Charleston, SC" : `Junk Removal in ${city.name}, SC`;
  const titleFull = isCharleston ? "Charleston, SC Junk Hauling & Cleanouts | Skedaddle" : undefined;
  const desc = `Fast, friendly junk removal in ${city.name}, SC. Skedaddle hauls furniture, appliances, cleanouts and debris across ${city.county}. Get a free quote!`;
  const serviceLd = {
    "@context": "https://schema.org", "@type": "Service", name: title,
    serviceType: "Junk Removal", areaServed: { "@type": "City", name: `${city.name}, SC` },
    provider: { "@type": "HomeAndConstructionBusiness", name: "Skedaddle Junk Removal", telephone: "+1-843-518-0021" },
    description: desc,
  };

  return (
    <>
      <Seo path={cityPath(city)} title={title} titleFull={titleFull} description={desc} jsonLd={[serviceLd, breadcrumbLd(crumbs)]} />

      {/* Hero */}
      <section className="bg-white border-b-2 border-charcoal py-12 md:py-16">
        <div className="mx-auto max-w-5xl px-4 md:px-8">
          <Breadcrumbs items={crumbs} />
          <div className="inline-flex items-center gap-2 bg-teal text-cream border-2 border-charcoal rounded-full px-4 py-1.5 shadow-brutsm font-body font-bold text-sm mt-6"><MapPin className="w-4 h-4" /> {city.county}</div>
          <h1 className="font-heading font-bold text-4xl md:text-6xl tracking-tight text-charcoal mt-4">{title}</h1>
          <p className="mt-4 font-body text-lg md:text-xl text-charcoal/75 max-w-2xl">{content.intro[0]}</p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Btn to="/contact" variant="primary" data-testid="city-quote-btn">{CTA.quote}</Btn>
            <Btn href={BRAND.phoneHref} variant="secondary"><Phone className="w-5 h-5" /> {BRAND.phoneDisplay}</Btn>
          </div>
        </div>
      </section>

      {/* Intro body + services */}
      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-5xl px-4 md:px-8 grid md:grid-cols-2 gap-10">
          <Reveal>
            <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tight">Your local {city.name} junk crew</h2>
            <div className="mt-4 space-y-4 font-body text-lg text-charcoal/75">{content.intro.map((p, i) => <p key={i}>{p}</p>)}</div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="bg-cream border-2 border-charcoal rounded-3xl p-7 shadow-brut">
              <h3 className="font-heading font-bold text-2xl">What we haul in {city.name}</h3>
              <div className="mt-4 grid grid-cols-2 gap-2">
                {TOP_SERVICES.slice(0, 8).map((s) => (
                  <Link key={s.slug} to={servicePath(s)} className="flex items-center gap-2 font-body font-semibold text-sm hover:text-coral"><span className="w-5 h-5 rounded-full bg-teal text-cream flex items-center justify-center shrink-0"><Check className="w-3 h-3" /></span>{s.title}</Link>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Neighborhoods */}
      <section className="bg-charcoal text-cream py-16 md:py-20">
        <div className="mx-auto max-w-6xl px-4 md:px-8">
          <Reveal>
            <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tight">Neighborhoods we serve in {city.name}</h2>
            <p className="mt-3 font-body text-lg text-cream/70 max-w-2xl">We know these streets! Here are the areas of {city.name} where we haul junk, and there are plenty more. If you do not see yours, just ask.</p>
          </Reveal>
          <Stagger className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {content.neighborhoods.map(([name, note]) => (
              <motion.div key={name} variants={item}>
                <Link to={`/service-areas/${slug}/${hoodSlug(name)}`} data-testid={`hood-link-${hoodSlug(name)}`}
                  className="block border border-cream/20 rounded-2xl p-5 bg-cream/5 hover:bg-cream/15 transition-colors h-full">
                  <div className="flex items-center gap-2 font-heading font-bold text-lg"><MapPin className="w-4 h-4 text-sunny" /> {name}</div>
                  <p className="mt-1 font-body text-cream/65 text-sm">{note}</p>
                </Link>
              </motion.div>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Nearby cities */}
      <section className="bg-cream py-16 md:py-20 border-y-2 border-charcoal">
        <div className="mx-auto max-w-6xl px-4 md:px-8">
          <h2 className="font-heading font-bold text-3xl tracking-tight">We are nearby too</h2>
          <div className="mt-8 grid gap-5 sm:grid-cols-3">
            {nearby.map((c) => (
              <Link key={c.slug} to={cityPath(c)} data-testid={`nearby-${c.slug}`} className="group flex items-center justify-between bg-white border-2 border-charcoal rounded-2xl px-5 py-4 shadow-brut hover:bg-sunny transition-colors">
                <span className="font-heading font-bold text-lg flex items-center gap-2"><MapPin className="w-5 h-5" /> {c.name}</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            ))}
          </div>
          <Link to="/service-areas" className="mt-6 inline-flex items-center gap-1 font-body font-bold text-coral">See all service areas <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-coral text-cream py-16 md:py-20 text-center">
        <div className="mx-auto max-w-2xl px-4 md:px-8">
          <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight">Junk gone in {city.name}!</h2>
          <p className="mt-3 font-body text-lg text-cream/90">Free quotes, friendly crew, no pressure. One call and it skedaddles!</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Btn to="/contact" variant="accent">{CTA.quote}</Btn>
            <Btn href={BRAND.smsHref} variant="white">Text us</Btn>
          </div>
        </div>
      </section>
    </>
  );
}
